/* eslint-disable react/no-danger */
import {
  Box,
  FeatureIcon,
  Flex,
  IconButton,
  ProfilePhoto,
  Text,
  ThumbsUpLikeIcon,
  Tooltip,
  palette,
} from "@minervaproject/fds";
import { MathJax } from "better-react-mathjax";
import { format, formatDistanceToNow } from "date-fns";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactTooltip from "react-tooltip";
import { useHover } from "usehooks-ts";

import { ChatInput } from "./chat_input";
import { EmojiConverter } from "./helpers";

import { FlightErrorBoundary } from "components/error_boundary";
import { useCapability } from "hooks/capabilities";
import { useAppDispatch, useAppSelector, useArraySelector } from "hooks/redux_hooks";
import { useUsersProperties } from "hooks/use_user_properties";
import {
  addUserToParticipantBarAndToBestPaneOnStage,
  likeOrUnlikeChat,
  setFeaturedInModal,
} from "shared/reducers/classroom";
import { CapabilityType, ChatType } from "shared/types";
import { RoleType, UserType } from "shared/types/user";
import { classroomSelectors, localSelectors } from "store";
import { escapeRegex } from "utils/string";
import { computeInitials, longDisplayName, profilePhotoUrl, shortDisplayName } from "utils/user";

import "./chat.scss";

const URL_REGEX =
  "https?:\\/\\/(?:www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{2,256}\\.[a-z]{2,4}\\b(?:[-a-zA-Z0-9@:%_\\+.~#?&//=]*)";
function ProcessedWord({
  word,
  usersAsTags,
  userCanMentionEveryoneInChat,
}: {
  word: string;
  usersAsTags: string[];
  userCanMentionEveryoneInChat: boolean;
}) {
  if (!word) return null;
  if (
    (userCanMentionEveryoneInChat && word.toLowerCase() === "@all") ||
    word.toLowerCase() === "@captioner" ||
    usersAsTags.includes(escapeRegex(`${word}`.toLowerCase()))
  ) {
    return <span className="bold">{`${word} `}</span>;
  }
  if (
    word.startsWith(":") &&
    word.endsWith(":") &&
    EmojiConverter.aliases.includes(word.slice(1, -1))
  ) {
    return (
      <div
        className="emoji-img-div emoji-chat-img"
        dangerouslySetInnerHTML={{ __html: EmojiConverter.replace_colons(word) }}
      />
    );
  }
  if (word.match(new RegExp(URL_REGEX))) {
    return <a href={word} target="_blank" rel="noreferrer">{`${word} `}</a>;
  }
  return <>{word} </>;
}

function ProcessedChatMessage({
  chat,
  users,
  userCanMentionEveryoneInChat,
}: {
  chat: ChatType;
  users: { [id: string]: UserType };
  userCanMentionEveryoneInChat: boolean;
}) {
  const { text } = chat;
  const usersAsTags = Object.values(users)
    .map((user) => escapeRegex(`@${longDisplayName(user)}`.toLowerCase()))
    .sort();
  const allTags = [...usersAsTags, "@all", "@captioner"];
  // all tags ,emojis and urls followed by punctuation, space or end of string
  const tagsAndEmojiRegex = new RegExp(
    `(${allTags.join("|")}|:[-+\\w]+:|${URL_REGEX})(?=[.,!?;:()\\[\\]{}'"-]|\\s|$)`,
    "gi",
  );
  const textArray = text.split(tagsAndEmojiRegex);

  return (
    <MathJax>
      {textArray.map((word, index) => {
        if (word === undefined) {
          window.flightLogger.error(
            `[Chat] word is undefined: ${text}, ${index}, ${JSON.stringify(textArray)}`,
          );
          return null;
        }
        const key = `chat-${chat.id}-word-${index}`;
        return (
          <ProcessedWord
            key={key}
            word={word.trim()}
            usersAsTags={usersAsTags}
            userCanMentionEveryoneInChat={userCanMentionEveryoneInChat}
          />
        );
      })}
    </MathJax>
  );
}

type ChatMessageProps = {
  chatId: string;
  firstMessage: boolean;
  currentUserId: string;
  users: { [id: string]: UserType };
};

const ChatMessage = memo(({ chatId, firstMessage, currentUserId, users }: ChatMessageProps) => {
  const chat = useAppSelector(classroomSelectors.selectChatById(chatId)) as ChatType;
  const dispatch = useAppDispatch();
  const ref = useRef(null);
  const canControlClassroom = useCapability(CapabilityType.CAN_CONTROL_CLASSROOM);
  const isHovered = useHover(ref);
  const user = users[chat.user_id];
  const isMe = chat.user_id === currentUserId;
  const displayName = `${longDisplayName(user)}${
    user.role === RoleType.Captioner ? " (Captioner)" : ""
  }`;
  const userCanMentionEveryoneInChat = useCapability(CapabilityType.CAN_MENTION_EVERYONE_IN_CHAT, {
    userId: chat.user_id,
  });

  const createdOn = chat.created_on;
  const wasCreatedLessThan24HourAgo = createdOn > Date.now() - 24 * 60 * 60 * 1000;
  const timestamp = wasCreatedLessThan24HourAgo
    ? format(createdOn, "h:mmaaa")
    : formatDistanceToNow(createdOn);
  const voteCount = chat.likes.length;
  const hasVoted = chat.likes.includes(currentUserId);
  const votes = useMemo(() => {
    if (!chat.likes.length) return "";
    const names: string[] = [];
    chat.likes.forEach((likeUserId) => {
      if (likeUserId === currentUserId) {
        names.unshift("You");
      } else {
        names.push(shortDisplayName(users[likeUserId]));
      }
    });
    const lastVote = names.pop();
    if (names.length) {
      return `${names.join(", ")} and ${lastVote} liked this`;
    }
    return `${lastVote} liked this`;
  }, [chat.likes, users, currentUserId]);

  const showButtons = isHovered || voteCount > 0;

  const activeBreakoutSessionId = useAppSelector(
    classroomSelectors.selectActiveBreakoutSessionId(),
  );

  if (!user) return null;

  return (
    <div className="flex px2 pb2" ref={ref} data-testid="chat-message" style={{ gap: "12px" }}>
      <ProfilePhoto className="mt4" src={profilePhotoUrl(user)} initials={computeInitials(user)} />
      <div
        className="pt3 flex-1-overflow-hidden"
        style={{ borderTop: !firstMessage ? "1px solid var(--black-tint-40)" : "none" }}
      >
        <div className="flex items-center justify-between mb1" style={{ height: "18px" }}>
          <div className="flex text-black-tint-70 flex-1-overflow-hidden">
            <Text
              bold
              kind="body-xs"
              className="flex-truncate"
              title={displayName}
              data-testid="chat-message-user-name"
            >
              {displayName}
            </Text>
            <Text kind="body-xs" mx={1}>
              &middot;
            </Text>
            <Text kind="body-xs" aria-hidden="true" className="truncate">
              {timestamp}
            </Text>
          </div>
          <div className="flex items-center" style={{ flex: 0 }}>
            {canControlClassroom && showButtons && (
              <div
                className="flex items-center"
                onClick={() => {
                  dispatch(setFeaturedInModal({ contentId: chat.id }));
                  dispatch(
                    addUserToParticipantBarAndToBestPaneOnStage({
                      userId: chat.user_id,
                      pinUserToStage: true,
                    }),
                  );
                }}
                style={{ marginTop: "3px" }}
              >
                <Tooltip
                  backgroundColor={palette.blackTint20}
                  iconComponent={
                    <IconButton
                      disabled={!!activeBreakoutSessionId}
                      aria-label="Display this message to class"
                      data-testid={`feature-chat-button-${chat.id}`}
                    >
                      <FeatureIcon width={13} height={13} color={palette.blackTint70} />
                    </IconButton>
                  }
                >
                  {`Display this message to class${
                    activeBreakoutSessionId ? " (disabled during breakout)" : ""
                  }`}
                </Tooltip>
              </div>
            )}

            <div
              className={`flex items-center${!(isHovered || voteCount > 0) ? " display-none" : ""}`}
              color={hasVoted ? "green" : "blackTint70"}
              style={showButtons ? (isMe ? { opacity: 0.7 } : { opacity: 1 }) : { opacity: 0 }}
              onClick={() => {
                dispatch(likeOrUnlikeChat({ userId: currentUserId, id: chat.id }));
              }}
              data-for="chat"
              data-tip={votes}
            >
              <IconButton disabled={isMe} aria-label="Like message">
                <ThumbsUpLikeIcon
                  width={13}
                  height={13}
                  color={hasVoted ? palette.green : palette.blackTint70}
                />
              </IconButton>
              <Text className="cursor-pointer" kind="body-xs">
                {voteCount}
              </Text>
            </div>
            {votes && <ReactTooltip id="chat" effect="solid" />}
          </div>
        </div>

        <FlightErrorBoundary text="This message cannot be displayed" margin="0">
          <Text kind="body-s" className="wrap-words" data-testid="chat-message-text">
            <ProcessedChatMessage
              chat={chat}
              users={users}
              userCanMentionEveryoneInChat={userCanMentionEveryoneInChat}
            />
          </Text>
        </FlightErrorBoundary>
      </div>
    </div>
  );
});

function Chat() {
  const chatContainerRef = useRef<HTMLElement | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);

  const chatIds = useArraySelector(classroomSelectors.selectChatIds());
  const users = useUsersProperties(["first_name", "last_name", "is_present", "role"]);
  const chatCount = chatIds.length;
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());

  const scrollToBottom = useCallback(() => {
    if (!chatContainerRef.current) return;
    try {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        // @ts-ignore
        behavior: "instant",
      });
    } catch (error) {
      window.flightLogger.error("Failed to scroll to bottom of chat", error);
    }
  }, []);

  const handleScroll = useCallback(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    // If the user scrolled to the bottom, update the state
    const isUserAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight;
    setIsAtBottom(isUserAtBottom);
  }, []);

  useEffect(() => {
    const container = chatContainerRef.current;
    container?.addEventListener("scroll", handleScroll);

    // Cleanup event listener on component unmount
    return () => {
      container?.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    if (isAtBottom) {
      scrollToBottom();
    }
    // Only scroll to bottom when chat count changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatCount]);

  return (
    <Flex className="chat sidebar-width">
      <Flex direction="column" width="100%">
        <Text className="body-l flex-none" kind="h2" p={2}>
          Class Chat
        </Text>
        <Box
          className="vertical-scroll flex-auto"
          pb={2}
          aria-live="polite"
          aria-atomic="false"
          aria-relevant="additions text"
          ref={chatContainerRef}
        >
          {chatIds.map((chatId, index) => (
            <FlightErrorBoundary
              key={chatId}
              text="This message cannot be displayed"
              margin="0 8px 8px"
            >
              <ChatMessage
                chatId={chatId}
                firstMessage={index === 0}
                currentUserId={currentUserId}
                users={users}
              />
            </FlightErrorBoundary>
          ))}
          <Box ref={chatEndRef} />
        </Box>
        <ChatInput />
      </Flex>
    </Flex>
  );
}

export { ProcessedWord, ProcessedChatMessage, ChatMessage, Chat };
